import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable, filtersText } from "../ask/reportAsk.utils";
import {
  AgeBy,
  AGING_BUCKETS,
  AGING_LABELS,
  AgingBucket,
  agingByClient,
  formatMoney,
  percent,
} from "../reports.utils";
import { useViewExtra } from "../saved/SavedViewContext";
import {
  ChoiceMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Avatar,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  Legend,
  LoadError,
  Loading,
  NUM,
  ReportPage,
  StackedBar,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllInvoices } from "../useReportData";

const COLORS: Record<AgingBucket, string> = {
  notDue: "#16A34A",
  d0: "#6B4EFF",
  d31: "#A898FF",
  d61: "#E08A1E",
  d91: "#D92D20",
};

const AGE_BY: { value: AgeBy; label: string }[] = [
  { value: "dueDate", label: "Due date" },
  { value: "invoiceDate", label: "Invoice date" },
];

export const AgingReport: FC = () => {
  const state = useReportFilters();
  const { filters } = state;
  const [ageBy, setAgeBy] = useViewExtra<AgeBy>(
    "ageBy",
    "dueDate",
    AGE_BY,
    "Age by",
  );
  const invoices = useAllInvoices({
    customerIds: filters.clientIds,
    userIds: filters.userIds,
  });
  const data = useMemo(
    () => agingByClient(invoices.data, ageBy),
    [invoices.data, ageBy],
  );
  const buckets = AGING_BUCKETS.filter(
    (b) => ageBy === "dueDate" || b !== "notDue",
  );
  const v = (x: React.ReactNode) => (invoices.isLoading ? "…" : x);
  const tone = (b: AgingBucket) =>
    b === "d91"
      ? "red"
      : b === "d61"
      ? "amber"
      : b === "notDue"
      ? "green"
      : "default";

  const onExport = () =>
    exportDataToExcel(
      data.rows.map((r) => ({
        Client: r.name,
        ...Object.fromEntries(
          buckets.map((b) => [
            AGING_LABELS[b],
            Math.round(r.buckets[b] * 100) / 100,
          ]),
        ),
        Total: Math.round(r.total * 100) / 100,
      })),
      "Unpaid invoices by age",
    );

  const tableName = "By client, largest balance first";
  const shownRows = useFocusRows(tableName, data.rows, (r) => r.customerId);
  const { pageRows, pager } = usePagedRows(shownRows);

  const ask = useMemo(() => {
    const bucketLabel = (b: AgingBucket) =>
      ageBy === "dueDate" && b !== "notDue"
        ? `${AGING_LABELS[b]} late`
        : AGING_LABELS[b];
    return {
      title: "Unpaid invoices by age",
      path: "/reports/aging",
      description:
        ageBy === "dueDate"
          ? "What clients owe, grouped by how many days the invoices are past their due date."
          : "What clients owe, grouped by how many days ago the invoices were dated.",
      filters: filtersText([
        `Age by: ${AGE_BY.find((a) => a.value === ageBy)?.label}`,
      ]),
      kpis: [
        {
          label: "Total unpaid",
          value: `${formatMoney(data.total)} (${data.count} invoices)`,
        },
        ...buckets.map((b) => ({
          label: bucketLabel(b),
          value: `${formatMoney(data.totals[b])} (${percent(
            data.totals[b],
            data.total,
          )}%)`,
        })),
      ],
      tables: [
        askTable(
          tableName,
          data.rows,
          [
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (r) => r.name,
            },
            ...buckets.map((b) => ({
              key: b,
              label: AGING_LABELS[b],
              kind: "money" as const,
              value: (r: typeof data.rows[number]) => r.buckets[b],
            })),
            {
              key: "total",
              label: "Total",
              kind: "money",
              value: (r) => r.total,
            },
          ],
          (r) => r.customerId,
        ),
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ageBy]);

  return (
    <ReportPage
      title={"Unpaid invoices by age"}
      description={
        ageBy === "dueDate"
          ? "What clients owe, grouped by how many days the invoices are past their due date."
          : "What clients owe, grouped by how many days ago the invoices were dated."
      }
      onExport={data.rows.length ? onExport : undefined}
      ask={invoices.isLoading ? undefined : ask}
      askSuggestions={[
        "Which clients owe us the most?",
        "Who has invoices more than 90 days late?",
        "How much of the unpaid amount is overdue?",
      ]}
      filters={
        <>
          <ChoiceMenu
            label={"Age by"}
            options={AGE_BY}
            value={ageBy}
            onChange={setAgeBy}
          />
          <PeopleClientTypeFilters
            state={state}
            showWorkTypes={false}
            peopleLabel={"Assignee"}
          />
        </>
      }>
      {invoices.isError && <LoadError />}
      <KpiGrid min={170}>
        <Kpi
          label={"Total unpaid"}
          value={v(formatMoney(data.total))}
          note={`${data.count} invoices`}
        />
        {buckets.map((b) => (
          <Kpi
            key={b}
            label={
              ageBy === "dueDate" && b !== "notDue"
                ? `${AGING_LABELS[b]} late`
                : AGING_LABELS[b]
            }
            value={v(formatMoney(data.totals[b]))}
            note={`${percent(data.totals[b], data.total)}%`}
            tone={tone(b)}
          />
        ))}
      </KpiGrid>

      <Card
        title={"How old the unpaid amount is"}
        right={
          <Legend
            items={buckets.map((b) => ({
              label: AGING_LABELS[b],
              color: COLORS[b],
            }))}
          />
        }>
        {invoices.isLoading ? (
          <Loading />
        ) : invoices.isError ? null : !data.total ? (
          <Empty text={"Nothing is unpaid."} />
        ) : (
          <StackedBar
            height={22}
            max={data.total}
            parts={buckets.map((b) => ({
              value: data.totals[b],
              color: COLORS[b],
              label: AGING_LABELS[b],
            }))}
          />
        )}
      </Card>

      <Card title={"By client, largest balance first"}>
        {invoices.isLoading ? (
          <Loading />
        ) : invoices.isError ? null : !data.rows.length ? (
          <Empty text={"Nothing is unpaid."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                {buckets.map((b) => (
                  <th key={b} scope={"col"} className={`${TH} ${NUM}`}>
                    {AGING_LABELS[b]}
                  </th>
                ))}
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                <tr key={r.customerId}>
                  <td className={`${TD} font-bold`}>
                    <Avatar name={r.name} square />
                    {r.name}
                  </td>
                  {buckets.map((b) => (
                    <td
                      key={b}
                      className={`${TD} ${NUM} ${
                        r.buckets[b] ? "" : "text-[#8A92A3]"
                      } ${
                        b === "d91" && r.buckets[b]
                          ? "font-extrabold text-[#B42318]"
                          : ""
                      }`}>
                      {r.buckets[b] ? formatMoney(r.buckets[b]) : "—"}
                    </td>
                  ))}
                  <td className={`${TD} ${NUM} font-extrabold`}>
                    {formatMoney(r.total)}
                  </td>
                </tr>
              ))}
              <tr>
                <td className={`${TD} font-extrabold`}>Total</td>
                {buckets.map((b) => (
                  <td key={b} className={`${TD} ${NUM} font-extrabold`}>
                    {formatMoney(data.totals[b])}
                  </td>
                ))}
                <td className={`${TD} ${NUM} font-extrabold`}>
                  {formatMoney(data.total)}
                </td>
              </tr>
            </tbody>
          </TableBox>
        )}
      </Card>
    </ReportPage>
  );
};
